import * as React from 'react';
import { ProfileType, HcpGroup } from '@ixicare/resources';
import type { HcpGroupEntryProps, HcpGroupProfileSummary } from '@ixicare/resources';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Table, Select, Button } from '@ixicare/ui';
import { Plus, Copy, ClipboardPaste } from 'lucide-react';
import { observer } from 'mobx-react';
import { t } from '~/i18n';
import { useStores } from '~store/utils';
import { useHcpGroups } from '~hooks/useHcpGroups';
import { useSiteProfiles } from '~hooks/useSiteProfiles';
import RemovableChip from '~components/RemovableChip';

export const ResidentAssignmentTab: React.FC = observer(() => {
  const queryClient = useQueryClient();
  const { notifications, sites } = useStores();
  const siteUuid = sites.selectedSite?.uuid;

  const { data: groups } = useHcpGroups(siteUuid);
  const { data: allResidents } = useSiteProfiles(ProfileType.Resident);

  const [copiedSenderIds, setCopiedSenderIds] = React.useState<number[] | null>(null);

  const updateSendersMutation = useMutation({
    mutationFn: ({ groupId, alarmSenderIds }: { groupId: string; alarmSenderIds: number[]; successMessage: string }) =>
      HcpGroup.updateHcpGroup(groupId, { alarmSenderIds }),
    onSuccess: (updatedGroup, { successMessage }) => {
      queryClient.setQueryData<HcpGroupEntryProps[]>(['hcpGroups', siteUuid], (old) =>
        old?.map((group) => (group.id === updatedGroup.id ? updatedGroup : group))
      );
      notifications.addUserActionNotification({ notificationType: 'success', message: successMessage });
    },
    onError: () => {
      notifications.addUserActionNotification({
        notificationType: 'error',
        message: t('hcpGroups.assign.resident.error'),
      });
    },
  });

  const senderIdsOf = (group: HcpGroupEntryProps) => group.alarmSenders.map((s) => s.id);

  const addSender = (group: HcpGroupEntryProps, residentId: number) =>
    updateSendersMutation.mutate({
      groupId: group.id,
      alarmSenderIds: [...new Set([...senderIdsOf(group), residentId])],
      successMessage: t('hcpGroups.assign.resident.success'),
    });

  const removeSender = (group: HcpGroupEntryProps, residentId: number) =>
    updateSendersMutation.mutate({
      groupId: group.id,
      alarmSenderIds: senderIdsOf(group).filter((id) => id !== residentId),
      successMessage: t('hcpGroups.unassign.resident.success'),
    });

  const addSenders = (group: HcpGroupEntryProps, residentIds: number[]) =>
    updateSendersMutation.mutate({
      groupId: group.id,
      alarmSenderIds: [...new Set([...senderIdsOf(group), ...residentIds])],
      successMessage: t('hcpGroups.assign.resident.success'),
    });

  const handlePasteAll = (group: HcpGroupEntryProps) => {
    if (copiedSenderIds === null) return;
    addSenders(group, copiedSenderIds);
    setCopiedSenderIds(null);
  };

  return (
    <div>
      <p style={{ marginBottom: '20px', color: '#666' }}>{t('hcpGroups.tabs.assignResidents.description')}</p>
      {copiedSenderIds !== null && (
        <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ color: '#666' }}>{t('hcpGroups.copy.active', { count: copiedSenderIds.length })}</span>
          <Button buttonType="inverse" onClick={() => setCopiedSenderIds(null)}>
            {t('hcpGroups.copy.cancel')}
          </Button>
        </div>
      )}
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.HeadCol>{t('hcpGroups.table.groupName')}</Table.HeadCol>
            <Table.HeadCol>{t('hcpGroups.assigned.residents')}</Table.HeadCol>
            <Table.HeadCol>{t('hcpGroups.add.resident')}</Table.HeadCol>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {Array.isArray(groups) &&
            groups.map((group) => {
              const senderIds = senderIdsOf(group);
              return (
                <Table.Row key={group.id}>
                  <Table.Col>{group.hcpGroupName}</Table.Col>
                  <Table.Col>
                    <GroupResidents
                      senders={group.alarmSenders}
                      onRemove={(residentId) => removeSender(group, residentId)}
                    />
                  </Table.Col>
                  <Table.Col>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <ResidentSelectorWithAddAll
                        allResidents={allResidents || []}
                        assignedSenderIds={senderIds}
                        onAssign={(residentId) => addSender(group, residentId)}
                        onAssignAll={(residentIds) => addSenders(group, residentIds)}
                      />
                      {copiedSenderIds === null ? (
                        <Button
                          buttonType="inverse"
                          lucide={Copy}
                          disabled={senderIds.length === 0}
                          onClick={() => setCopiedSenderIds(senderIds)}
                          style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                        >
                          {t('hcpGroups.copyAll.button')}
                        </Button>
                      ) : (
                        <Button
                          buttonType="inverse"
                          lucide={ClipboardPaste}
                          disabled={copiedSenderIds.every((id) => senderIds.includes(id))}
                          onClick={() => handlePasteAll(group)}
                          style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                        >
                          {t('hcpGroups.pasteAll.button')}
                        </Button>
                      )}
                    </div>
                  </Table.Col>
                </Table.Row>
              );
            })}
        </Table.Body>
      </Table>
    </div>
  );
});

// Display assigned senders with remove buttons
function GroupResidents({
  senders,
  onRemove,
}: {
  senders: HcpGroupProfileSummary[];
  onRemove: (residentId: number) => void;
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
      {senders.length === 0 ? (
        <span style={{ color: '#999' }}>{t('hcpGroups.no.residents')}</span>
      ) : (
        senders.map((sender) => (
          <RemovableChip
            key={sender.id}
            onRemove={() => onRemove(sender.id)}
            removeTitle={t('hcpGroups.unassign.resident.label')}
          >
            {sender.fullName}
          </RemovableChip>
        ))
      )}
    </div>
  );
}

// Resident selector dropdown with Add All button
function ResidentSelectorWithAddAll({
  allResidents,
  assignedSenderIds,
  onAssign,
  onAssignAll,
}: {
  allResidents: any[];
  assignedSenderIds: number[];
  onAssign: (residentId: number) => void;
  onAssignAll: (residentIds: number[]) => void;
}) {
  const [selectedResident, setSelectedResident] = React.useState<string>('');

  // Residents not yet assigned to this group
  const assignedIds = new Set(assignedSenderIds);
  const availableResidents = allResidents.filter((resident) => !assignedIds.has(resident.id));

  const handleChange = (value: string) => {
    if (value) {
      onAssign(parseInt(value));
      setSelectedResident('');
    }
  };

  const handleAddAll = () => {
    const residentIds = availableResidents.map((resident) => resident.id);
    if (residentIds.length > 0) {
      onAssignAll(residentIds);
    }
  };

  const options = [
    { value: '', label: t('hcpGroups.select.resident') },
    ...availableResidents.map((resident) => ({
      value: resident.id.toString(),
      label: `${resident.firstName} ${resident.lastName}`,
    })),
  ];

  return (
    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
      <Select
        value={selectedResident}
        onChange={handleChange}
        options={options}
        placeholder={t('hcpGroups.select.resident')}
      />
      <Button
        onClick={handleAddAll}
        lucide={Plus}
        disabled={availableResidents.length === 0}
        style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        {t('hcpGroups.addAll.button')}
      </Button>
    </div>
  );
}
